/**
 * 프로젝트 생성 페이지
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useLazyQuery } from "@apollo/client";
import { ArrowLeft } from "lucide-react";
import { createLogger, isTestCompanyLimitError } from "@ict/shared";
import { ProjectForm, type ProjectFormData } from "@/components/project/ProjectForm";
import { CREATE_PROJECT_MUTATION, PROJECTS_QUERY } from "@/lib/graphql/projects";
import { DEMO_STATUS, type DemoStatusData } from "@/lib/graphql/user";
import { DemoLimitModal } from "@/components/onboarding/DemoLimitModal";
import { useToast } from "@/contexts/ToastContext";

const logger = createLogger({ module: "ProjectCreatePage" });

export function ProjectCreatePage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [showLimitModal, setShowLimitModal] = useState(false);

  // 데모 상태 조회 (에러 시에만 호출)
  const [fetchDemoStatus, { data: demoData }] = useLazyQuery<DemoStatusData>(DEMO_STATUS);

  const [createProject, { loading }] = useMutation(CREATE_PROJECT_MUTATION, {
    refetchQueries: [{ query: PROJECTS_QUERY }, { query: DEMO_STATUS }],
    onCompleted: (data) => {
      showToast("프로젝트가 생성되었습니다.", "success");
      navigate(`/projects/${data.createProject._id}`);
    },
    onError: (error) => {
      logger.error("프로젝트 생성 실패:", error);
      if (isTestCompanyLimitError(error)) {
        fetchDemoStatus();
        setShowLimitModal(true);
      } else {
        showToast("프로젝트 생성에 실패했습니다.", "error");
      }
    },
  });

  const handleSubmit = async (formData: ProjectFormData) => {
    await createProject({
      variables: {
        input: formData,
      },
    });
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/projects");
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* 헤더 */}
      <div className="border-b bg-white px-4 py-3 flex items-center gap-3">
        <button
          onClick={handleBack}
          className="p-1.5 -ml-1.5 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
          aria-label="뒤로가기"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-lg font-bold">새 프로젝트</h1>
      </div>

      {/* 폼 */}
      <ProjectForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleBack}
        isLoading={loading}
      />

      {/* 데모 한도 안내 모달 */}
      <DemoLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        limitType="project"
        currentCount={demoData?.demoStatus?.projectCount}
        maxCount={demoData?.demoStatus?.projectLimit}
      />
    </div>
  );
}

export default ProjectCreatePage;
